/* eslint max-lines: ["error", { "max": 610 }] */
import {
  EmployeesReportColumn,
  EmployeesReportRowDto,
  EmployeesReportView,
  EmployeesReportViewCountsDto,
  GenerateEmployeesReportDto,
  OpenEnrollmentPeriodDto,
} from '@zorro/clients';
import {
  APP_ROUTES,
  calculateAge,
  calculateAgeForEnrollment,
  convertToMaybeYesNo,
  formatSsn,
  getSubEntityLabel,
  isDefined,
} from '@zorro/common-utils';
import {
  formatCurrencyEnUs,
  formatToFixedFloat,
  getNowAsDate,
  getToday,
  parseDateISOThenFormatEnLocale,
  parseDateTimeISOThenFormatEnLocale,
  tryParseDateISOThenFormatEnLocale,
} from '@zorro/shared/formatters';
import {
  PolicyTypeLabelConfig,
  employeeRoleLabelConfig,
  employerFamilyUnitLabelConfig,
  employmentTypeLabelConfig,
  onboardingTypeLabelConfig,
  paymentMethodLabelConfig,
  qualifyingLifeEventLabelConfig,
  selfEnrollTypeLabelConfig,
  selfReportTypeLabelConfig,
  submissionTypeToStringMap,
} from '@zorro/types';
import { exportToCsv, hasIncompleteQleElection } from '@zorro/web/ui-utils';
import { $enum } from 'ts-enum-util';

import { agentBadgeConfig } from '../AgentStatusBadge';
import { fetchAllReportRecords } from '../DatatableUtils';
import { benefitEligibilityLabelConfig } from '../EligibilityStatusBadge/EligibilityStatusBadgeUtils';
import { getEnrollmentStatusView } from '../EnrollmentStatus';
import { autoPayStatusToProps, initialPremiumStatusToProps } from '../Payments';
import { PENDING_ELECTION_LABEL } from '../PendingElection';
import { getPlanMarketIconLabelConfig } from '../PlanBadge';

export function getAvailableViews(
  selectedEnrollmentPeriod: OpenEnrollmentPeriodDto | null,
  counts?: EmployeesReportViewCountsDto
) {
  const isOpenEnrollmentMode = getToday().isBefore(
    selectedEnrollmentPeriod?.effectiveFrom
  );

  return isOpenEnrollmentMode
    ? [
        {
          label: 'All',
          value: EmployeesReportView.ALL,
          count: counts?.allCount || 0,
        },
        {
          label: 'Open enrollment',
          value: EmployeesReportView.OPEN_ENROLLMENT,
          count: counts?.openEnrollmentCount || 0,
        },
      ]
    : [
        {
          label: 'All',
          value: EmployeesReportView.ALL,
          count: counts?.allCount || 0,
        },
        {
          label: 'Latest coverage',
          value: EmployeesReportView.LATEST_COVERAGE,
          count: counts?.latestCoverageCount || 0,
        },
        {
          label: 'Enrollments in process',
          value: EmployeesReportView.ENROLLMENTS_IN_PROCESS,
          count: counts?.enrollmentsInProcessCount || 0,
        },
      ];
}

export const getHiddenEmployeeReportColumns = (flags: {
  isDon: boolean;
  hasEmployeesWithIdFromEmployer: boolean;
  shouldHidePaymentStatuses: boolean;
  hasMultipleEnrollmentTeams: boolean;
  hasSubEntities: boolean;
  hasSubEntities2: boolean;
  shouldHideSsn: boolean;
}): EmployeesReportColumn[] => {
  const hiddenColumns: EmployeesReportColumn[] = [];

  if (flags.shouldHideSsn) {
    hiddenColumns.push(EmployeesReportColumn.ssn);
  }

  if (!flags.hasEmployeesWithIdFromEmployer) {
    hiddenColumns.push(EmployeesReportColumn.employeeIdFromEmployer);
  }

  if (flags.shouldHidePaymentStatuses) {
    hiddenColumns.push(
      EmployeesReportColumn.initialPremiumPaymentStatus,
      EmployeesReportColumn.autoPayStatus
    );
  }

  if (flags.isDon && !flags.hasMultipleEnrollmentTeams) {
    hiddenColumns.push(EmployeesReportColumn.enrollmentTeamName);
  }
  if (flags.isDon) {
    hiddenColumns.push(EmployeesReportColumn.enrollmentAgentName);
  }

  if (!flags.hasSubEntities) {
    hiddenColumns.push(EmployeesReportColumn.subEntityName);
  }
  if (!flags.hasSubEntities2) {
    hiddenColumns.push(EmployeesReportColumn.subEntityName2);
  }

  return hiddenColumns;
};

export const getEmployeeReportColumnTitle = (
  column: EmployeesReportColumn
): string => {
  return $enum.visitValue(column).with({
    // Display columns
    [EmployeesReportColumn.employeeFirstName]: () => 'First name',
    [EmployeesReportColumn.employeeLastName]: () => 'Last name',
    [EmployeesReportColumn.employeeFullName]: () => 'Full name',
    [EmployeesReportColumn.email]: () => 'Email',
    [EmployeesReportColumn.invitationStatus]: () => 'Invitation status',
    [EmployeesReportColumn.employeeRole]: () => 'Role',
    [EmployeesReportColumn.dateOfBirth]: () => 'Age',
    [EmployeesReportColumn.state]: () => 'State',
    [EmployeesReportColumn.class]: () => 'Class',
    [EmployeesReportColumn.benefitEligibility]: () => 'Benefit eligibility',
    [EmployeesReportColumn.enrollmentStatus]: () => 'Status',
    [EmployeesReportColumn.onboardingFrom]: () => 'Election start',
    [EmployeesReportColumn.onboardingUntil]: () => 'Election end',
    [EmployeesReportColumn.enrollmentEffectiveFrom]: () => 'Coverage start',
    [EmployeesReportColumn.enrollmentEffectiveUntil]: () => 'Coverage end',
    [EmployeesReportColumn.enrollmentType]: () => 'Type',
    [EmployeesReportColumn.qleType]: () => 'QLE',
    [EmployeesReportColumn.qleOccurredOn]: () => 'QLE',
    [EmployeesReportColumn.enrollmentSubmissionType]: () => 'Submission type',
    [EmployeesReportColumn.enrollmentSubmissionDate]: () => 'Submission date',
    [EmployeesReportColumn.medicalPlanType]: () => 'Plan type',
    [EmployeesReportColumn.medicalSelfEnroll]: () => 'Self-enroll',
    [EmployeesReportColumn.paymentMethodType]: () => 'Payment method',
    [EmployeesReportColumn.initialPremiumPaymentStatus]: () =>
      'Initial payment status',
    [EmployeesReportColumn.autoPayStatus]: () => 'Auto-pay status',
    [EmployeesReportColumn.medicalCarrier]: () => 'Carrier',
    [EmployeesReportColumn.medicalPlanName]: () => 'Plan name',
    [EmployeesReportColumn.medicalPlanId]: () => 'Plan ID',
    [EmployeesReportColumn.medicalPlanMarket]: () => 'Plan market',
    [EmployeesReportColumn.benefitFamilyUnit]: () => 'Family unit',
    [EmployeesReportColumn.dependentCount]: () => 'Children',
    [EmployeesReportColumn.medicalIsHsaEligible]: () => 'HSA eligible',
    [EmployeesReportColumn.medicalPremium]: () => 'Premium',
    [EmployeesReportColumn.medicalEmployerContribution]: () =>
      'Employer contribution',
    [EmployeesReportColumn.medicalEmployeeContribution]: () =>
      'Employee contribution',
    [EmployeesReportColumn.onboardingPeriodAllowance]: () => 'Allowance',
    [EmployeesReportColumn.enrollmentTeamId]: () => 'Enrollment team ID',
    [EmployeesReportColumn.enrollmentTeamName]: () => 'Enrollment team',
    [EmployeesReportColumn.enrollmentAgentEmail]: () => 'Enrollment agent',
    [EmployeesReportColumn.enrollmentAgentFirstName]: () => 'Enrollment agent',
    [EmployeesReportColumn.enrollmentAgentLastName]: () => 'Enrollment agent',
    [EmployeesReportColumn.enrollmentAgentName]: () => 'Enrollment agent',
    [EmployeesReportColumn.employeeId]: () => 'Zorro ID',
    [EmployeesReportColumn.employeeIdFromEmployer]: () => 'Employee ID',
    [EmployeesReportColumn.subEntityName]: () => getSubEntityLabel(),
    [EmployeesReportColumn.subEntityName2]: () => 'Sub-entity 2',
    [EmployeesReportColumn.ssn]: () => 'SSN',
    [EmployeesReportColumn.employmentType]: () => 'Employment type',
    [EmployeesReportColumn.hireDate]: () => 'Hire date',

    // Helper fields
    [EmployeesReportColumn.employerId]: () => 'Employer ID',
    [EmployeesReportColumn.openEnrollmentPeriodId]: () =>
      'Open enrollment period ID',
    [EmployeesReportColumn.openEnrollmentEffectiveFrom]: () =>
      'Coverage period start',
    [EmployeesReportColumn.openEnrollmentEffectiveUntil]: () =>
      'Coverage period end',
    [EmployeesReportColumn.userId]: () => 'User ID',
    [EmployeesReportColumn.onboardingPeriodId]: () => 'Onboarding period ID',
    [EmployeesReportColumn.onboardingPeriodIdKey]: () => 'Onboarding period ID',
    [EmployeesReportColumn.isSpecialEnrollment]: () => 'Special enrollment',
    [EmployeesReportColumn.onboardingPeriodIsActive]: () => 'Active enrollment',
    [EmployeesReportColumn.employeeEligibleFrom]: () =>
      'Eligibility start date',
    [EmployeesReportColumn.employeeEligibleUntil]: () => 'Eligibility end date',
    [EmployeesReportColumn.employeeTerminationDate]: () => 'Termination date',
    [EmployeesReportColumn.leaveOfAbsenceStartDate]: () =>
      'Leave of absence start',
    [EmployeesReportColumn.leaveOfAbsenceEndDate]: () => 'Leave of absence end',
    [EmployeesReportColumn.employeeCreatedAt]: () => 'Added on',
    [EmployeesReportColumn.hasPreviousEligibility]: () =>
      'Has previous eligibility',
    [EmployeesReportColumn.employeeCustomClassCategory]: () =>
      'Custom class category',
    [EmployeesReportColumn.benefitId]: () => 'Benefit ID',
    [EmployeesReportColumn.benefitType]: () => 'Benefit type',
    [EmployeesReportColumn.onboardingPeriodIsWaived]: () => 'Waived enrollment',
    [EmployeesReportColumn.benefitStatus]: () => 'Benefit status',
    [EmployeesReportColumn.medicalSelfPayAmount]: () => 'Self-pay',
    [EmployeesReportColumn.medicalIsCombinedPlan]: () => 'Combined plan',
    [EmployeesReportColumn.medicalSelfReportType]: () => 'Self-report type',
    [EmployeesReportColumn.qleId]: () => 'QLE ID',
    [EmployeesReportColumn.qleOtherReason]: () => 'QLE other reason',
    [EmployeesReportColumn.isOpenEnrollmentView]: () => 'Open enrollment view',
    [EmployeesReportColumn.isLatestCoverageView]: () => 'Latest coverage view',
    [EmployeesReportColumn.isEnrollmentsInProcessView]: () =>
      'Enrollments in process view',
    [EmployeesReportColumn.isAllView]: () => 'All view',
    [EmployeesReportColumn.address]: () => 'Address',
    [EmployeesReportColumn.phone]: () => 'Phone number',
    [EmployeesReportColumn.personalEmail]: () => 'Personal email',
  });
};

export const getEmployeeReportColumnDisplayValue = (
  column: EmployeesReportColumn,
  record: EmployeesReportRowDto
): string | null | undefined => {
  return $enum.visitValue(column).with({
    // Display columns
    [EmployeesReportColumn.employeeFirstName]: () => record.employeeFirstName,
    [EmployeesReportColumn.employeeLastName]: () => record.employeeLastName,
    [EmployeesReportColumn.employeeFullName]: () => record.employeeFullName,
    [EmployeesReportColumn.email]: () => record.email,
    [EmployeesReportColumn.invitationStatus]: () =>
      agentBadgeConfig[record.invitationStatus].label,
    [EmployeesReportColumn.employeeRole]: () =>
      employeeRoleLabelConfig[record.employeeRole],
    [EmployeesReportColumn.dateOfBirth]: () =>
      record.openEnrollmentEffectiveFrom
        ? calculateAgeForEnrollment(
            record.dateOfBirth,
            record.openEnrollmentEffectiveFrom
          ).toString()
        : calculateAge(record.dateOfBirth).toString(),
    [EmployeesReportColumn.state]: () => record.state,
    [EmployeesReportColumn.class]: () => record.class,
    [EmployeesReportColumn.benefitEligibility]: () =>
      record.benefitEligibility
        ? benefitEligibilityLabelConfig[record.benefitEligibility]
        : null,
    [EmployeesReportColumn.enrollmentStatus]: () =>
      getEnrollmentStatusView(record.enrollmentStatus).label,
    [EmployeesReportColumn.onboardingFrom]: () =>
      tryParseDateISOThenFormatEnLocale(record.onboardingFrom),
    [EmployeesReportColumn.onboardingUntil]: () =>
      tryParseDateISOThenFormatEnLocale(record.onboardingUntil),
    [EmployeesReportColumn.enrollmentEffectiveFrom]: () =>
      hasIncompleteQleElection(record.enrollmentStatus, record.qleType)
        ? PENDING_ELECTION_LABEL
        : tryParseDateISOThenFormatEnLocale(record.enrollmentEffectiveFrom),
    [EmployeesReportColumn.enrollmentEffectiveUntil]: () =>
      tryParseDateISOThenFormatEnLocale(record.enrollmentEffectiveUntil),
    [EmployeesReportColumn.enrollmentType]: () =>
      record.enrollmentType
        ? onboardingTypeLabelConfig[record.enrollmentType]
        : null,
    [EmployeesReportColumn.qleType]: () =>
      record.qleType && record.qleOccurredOn
        ? `${qualifyingLifeEventLabelConfig[record.qleType]}, ${parseDateISOThenFormatEnLocale(record.qleOccurredOn)}`
        : null,
    [EmployeesReportColumn.qleOccurredOn]: () =>
      record.qleType && record.qleOccurredOn
        ? `${qualifyingLifeEventLabelConfig[record.qleType]}, ${parseDateISOThenFormatEnLocale(record.qleOccurredOn)}`
        : null,
    [EmployeesReportColumn.enrollmentSubmissionType]: () =>
      record.enrollmentSubmissionType
        ? submissionTypeToStringMap[record.enrollmentSubmissionType]
        : null,
    [EmployeesReportColumn.enrollmentSubmissionDate]: () =>
      record.enrollmentSubmissionDate
        ? parseDateTimeISOThenFormatEnLocale(record.enrollmentSubmissionDate)
        : null,
    [EmployeesReportColumn.medicalPlanType]: () =>
      !record.onboardingPeriodIsWaived && record.medicalPlanType
        ? PolicyTypeLabelConfig[record.medicalPlanType]
        : null,
    [EmployeesReportColumn.medicalSelfEnroll]: () =>
      record.medicalSelfEnroll
        ? selfEnrollTypeLabelConfig[record.medicalSelfEnroll]
        : null,
    [EmployeesReportColumn.paymentMethodType]: () =>
      record.paymentMethodType
        ? paymentMethodLabelConfig[record.paymentMethodType]
        : null,
    [EmployeesReportColumn.initialPremiumPaymentStatus]: () =>
      record.initialPremiumPaymentStatus
        ? initialPremiumStatusToProps[record.initialPremiumPaymentStatus].label
        : null,
    [EmployeesReportColumn.autoPayStatus]: () =>
      record.autoPayStatus
        ? autoPayStatusToProps[record.autoPayStatus].label
        : null,
    [EmployeesReportColumn.medicalCarrier]: () => record.medicalCarrier,
    [EmployeesReportColumn.medicalPlanName]: () => record.medicalPlanName,
    [EmployeesReportColumn.medicalPlanId]: () => record.medicalPlanId,
    [EmployeesReportColumn.medicalPlanMarket]: () =>
      record.medicalPlanMarket
        ? getPlanMarketIconLabelConfig(record.medicalPlanMarket).label
        : null,
    [EmployeesReportColumn.benefitFamilyUnit]: () =>
      record.benefitFamilyUnit
        ? employerFamilyUnitLabelConfig[record.benefitFamilyUnit]
        : null,
    [EmployeesReportColumn.dependentCount]: () =>
      isDefined(record.dependentCount)
        ? record.dependentCount.toString()
        : null,
    [EmployeesReportColumn.medicalIsHsaEligible]: () =>
      convertToMaybeYesNo(record.medicalIsHsaEligible),
    [EmployeesReportColumn.medicalPremium]: () =>
      isDefined(record.medicalPremium)
        ? formatCurrencyEnUs(formatToFixedFloat(record.medicalPremium))
        : null,
    [EmployeesReportColumn.medicalEmployerContribution]: () =>
      isDefined(record.medicalEmployerContribution)
        ? formatCurrencyEnUs(
            formatToFixedFloat(record.medicalEmployerContribution)
          )
        : null,
    [EmployeesReportColumn.medicalEmployeeContribution]: () =>
      isDefined(record.medicalEmployeeContribution)
        ? formatCurrencyEnUs(
            formatToFixedFloat(record.medicalEmployeeContribution)
          )
        : null,
    [EmployeesReportColumn.onboardingPeriodAllowance]: () =>
      record.onboardingPeriodAllowance
        ? formatCurrencyEnUs(record.onboardingPeriodAllowance)
        : null,
    [EmployeesReportColumn.enrollmentTeamId]: () => record.enrollmentTeamId,
    [EmployeesReportColumn.enrollmentTeamName]: () => record.enrollmentTeamName,
    [EmployeesReportColumn.enrollmentAgentEmail]: () =>
      record.enrollmentAgentName && record.enrollmentAgentEmail
        ? `${record.enrollmentAgentName} (${record.enrollmentAgentEmail})`
        : null,
    [EmployeesReportColumn.enrollmentAgentFirstName]: () =>
      record.enrollmentAgentFirstName,
    [EmployeesReportColumn.enrollmentAgentLastName]: () =>
      record.enrollmentAgentLastName,
    [EmployeesReportColumn.enrollmentAgentName]: () =>
      record.enrollmentAgentName,
    [EmployeesReportColumn.employeeId]: () =>
      record.employeeId ? record.employeeId.slice(0, 8) : null,
    [EmployeesReportColumn.employeeIdFromEmployer]: () =>
      record.employeeIdFromEmployer,
    [EmployeesReportColumn.subEntityName]: () => record.subEntityName,
    [EmployeesReportColumn.subEntityName2]: () => record.subEntityName2,
    [EmployeesReportColumn.ssn]: () =>
      record.ssn ? formatSsn(record.ssn) : null,
    [EmployeesReportColumn.employmentType]: () =>
      record.employmentType
        ? employmentTypeLabelConfig[record.employmentType]
        : null,
    [EmployeesReportColumn.hireDate]: () =>
      tryParseDateISOThenFormatEnLocale(record.hireDate),

    // Helper fields
    [EmployeesReportColumn.employerId]: () => record.employerId,
    [EmployeesReportColumn.openEnrollmentPeriodId]: () =>
      record.openEnrollmentPeriodId,
    [EmployeesReportColumn.openEnrollmentEffectiveFrom]: () =>
      tryParseDateISOThenFormatEnLocale(record.openEnrollmentEffectiveFrom),
    [EmployeesReportColumn.openEnrollmentEffectiveUntil]: () =>
      tryParseDateISOThenFormatEnLocale(record.openEnrollmentEffectiveUntil),
    [EmployeesReportColumn.userId]: () => record.userId,
    [EmployeesReportColumn.onboardingPeriodId]: () => record.onboardingPeriodId,
    [EmployeesReportColumn.onboardingPeriodIdKey]: () =>
      record.onboardingPeriodId,
    [EmployeesReportColumn.isSpecialEnrollment]: () =>
      convertToMaybeYesNo(record.isSpecialEnrollment),
    [EmployeesReportColumn.onboardingPeriodIsActive]: () =>
      convertToMaybeYesNo(record.onboardingPeriodIsActive),
    [EmployeesReportColumn.employeeEligibleFrom]: () =>
      tryParseDateISOThenFormatEnLocale(record.employeeEligibleFrom),
    [EmployeesReportColumn.employeeEligibleUntil]: () =>
      tryParseDateISOThenFormatEnLocale(record.employeeEligibleUntil),
    [EmployeesReportColumn.employeeTerminationDate]: () =>
      tryParseDateISOThenFormatEnLocale(record.employeeTerminationDate),
    [EmployeesReportColumn.leaveOfAbsenceStartDate]: () =>
      tryParseDateISOThenFormatEnLocale(record.leaveOfAbsenceStartDate),
    [EmployeesReportColumn.leaveOfAbsenceEndDate]: () =>
      tryParseDateISOThenFormatEnLocale(record.leaveOfAbsenceEndDate),
    [EmployeesReportColumn.employeeCreatedAt]: () =>
      tryParseDateISOThenFormatEnLocale(record.employeeCreatedAt),
    [EmployeesReportColumn.hasPreviousEligibility]: () =>
      convertToMaybeYesNo(record.hasPreviousEligibility),
    [EmployeesReportColumn.employeeCustomClassCategory]: () =>
      record.customClassCategory,
    [EmployeesReportColumn.benefitId]: () => record.benefitId,
    [EmployeesReportColumn.benefitType]: () => record.benefitType,
    [EmployeesReportColumn.onboardingPeriodIsWaived]: () =>
      convertToMaybeYesNo(record.onboardingPeriodIsWaived),
    [EmployeesReportColumn.benefitStatus]: () => record.benefitStatus,
    [EmployeesReportColumn.medicalSelfPayAmount]: () =>
      isDefined(record.medicalSelfPayAmount)
        ? formatCurrencyEnUs(formatToFixedFloat(record.medicalSelfPayAmount))
        : null,
    [EmployeesReportColumn.medicalIsCombinedPlan]: () =>
      convertToMaybeYesNo(record.medicalIsCombinedPlan),
    [EmployeesReportColumn.medicalSelfReportType]: () =>
      record.medicalSelfReportType
        ? selfReportTypeLabelConfig[record.medicalSelfReportType]
        : null,
    [EmployeesReportColumn.qleId]: () => record.qleId,
    [EmployeesReportColumn.qleOtherReason]: () => record.qleOtherReason,
    [EmployeesReportColumn.isOpenEnrollmentView]: () => null,
    [EmployeesReportColumn.isLatestCoverageView]: () => null,
    [EmployeesReportColumn.isEnrollmentsInProcessView]: () => null,
    [EmployeesReportColumn.isAllView]: () => null,
    [EmployeesReportColumn.address]: () => record.address ?? null,
    [EmployeesReportColumn.phone]: () => record.phone ?? null,
    [EmployeesReportColumn.personalEmail]: () => record.personalEmail ?? null,
  });
};

const EMPLOYEE_REPORT_FIELDS_TO_EXPORT = [
  EmployeesReportColumn.employeeFirstName,
  EmployeesReportColumn.employeeLastName,
  EmployeesReportColumn.email,
  EmployeesReportColumn.ssn,
  EmployeesReportColumn.employmentType,
  EmployeesReportColumn.hireDate,
  EmployeesReportColumn.employeeTerminationDate,
  EmployeesReportColumn.invitationStatus,
  EmployeesReportColumn.dateOfBirth,
  EmployeesReportColumn.state,
  EmployeesReportColumn.class,
  EmployeesReportColumn.benefitEligibility,
  EmployeesReportColumn.employeeRole,
  EmployeesReportColumn.enrollmentStatus,
  EmployeesReportColumn.onboardingFrom,
  EmployeesReportColumn.onboardingUntil,
  EmployeesReportColumn.enrollmentEffectiveFrom,
  EmployeesReportColumn.enrollmentEffectiveUntil,
  EmployeesReportColumn.enrollmentType,
  EmployeesReportColumn.qleOccurredOn,
  EmployeesReportColumn.enrollmentSubmissionType,
  EmployeesReportColumn.enrollmentSubmissionDate,
  EmployeesReportColumn.medicalPlanType,
  EmployeesReportColumn.medicalSelfEnroll,
  EmployeesReportColumn.paymentMethodType,
  EmployeesReportColumn.initialPremiumPaymentStatus,
  EmployeesReportColumn.autoPayStatus,
  EmployeesReportColumn.medicalCarrier,
  EmployeesReportColumn.medicalPlanName,
  EmployeesReportColumn.medicalPlanId,
  EmployeesReportColumn.medicalPlanMarket,
  EmployeesReportColumn.benefitFamilyUnit,
  EmployeesReportColumn.dependentCount,
  EmployeesReportColumn.medicalIsHsaEligible,
  EmployeesReportColumn.medicalPremium,
  EmployeesReportColumn.medicalEmployerContribution,
  EmployeesReportColumn.medicalEmployeeContribution,
  EmployeesReportColumn.onboardingPeriodAllowance,
  EmployeesReportColumn.employeeId,
  EmployeesReportColumn.employeeIdFromEmployer,
  EmployeesReportColumn.subEntityName,
  EmployeesReportColumn.subEntityName2,
  EmployeesReportColumn.address,
  EmployeesReportColumn.phone,
  EmployeesReportColumn.personalEmail,
  EmployeesReportColumn.enrollmentTeamName,
].map((column) => ({
  value: column as EmployeesReportColumn | string,
  label: getEmployeeReportColumnTitle(column),
})) as { value: EmployeesReportColumn | string; label: string }[];

const DATE_OF_BIRTH_RAW_KEY = 'dateOfBirthRaw';

EMPLOYEE_REPORT_FIELDS_TO_EXPORT.splice(
  EMPLOYEE_REPORT_FIELDS_TO_EXPORT.findIndex(
    (field) => field.value === EmployeesReportColumn.dateOfBirth
  ) + 1,
  0,
  { value: DATE_OF_BIRTH_RAW_KEY, label: 'Date of birth' }
);

const isEmployeesReportColumnEntry = (entry: {
  value: EmployeesReportColumn | string;
  label: string;
}): entry is { value: EmployeesReportColumn; label: string } =>
  Object.values(EmployeesReportColumn).some((col) => col === entry.value);

const transformForCsvExport =
  (fields: { value: EmployeesReportColumn | string; label: string }[]) =>
  (record: EmployeesReportRowDto): Record<string, string> => ({
    ...Object.fromEntries(
      fields.filter(isEmployeesReportColumnEntry).map(({ value: column }) => {
        const displayValue = getEmployeeReportColumnDisplayValue(
          column,
          record
        );
        return [column, isDefined(displayValue) ? displayValue : '-'];
      })
    ),
    [DATE_OF_BIRTH_RAW_KEY]: parseDateISOThenFormatEnLocale(record.dateOfBirth),
  });

export const downloadEmployees = async (
  employerName: string,
  employerId: string,
  selectedEnrollmentPeriodId: string,
  generateEmployeesReportDto: GenerateEmployeesReportDto,
  hiddenColumnIds?: string[]
) => {
  const timestamp = getNowAsDate().getTime();
  const fileName = `${employerName}_employees_enrollment_data_${timestamp}.csv`;

  const allEmployeesReportRecords =
    await fetchAllReportRecords<GenerateEmployeesReportDto>(
      'employeeReportsControllerGenerateEmployeesReport',
      [employerId, selectedEnrollmentPeriodId],
      generateEmployeesReportDto
    );

  const fieldsToExport = EMPLOYEE_REPORT_FIELDS_TO_EXPORT.filter(
    (field) => !hiddenColumnIds?.includes(field.value)
  );

  exportToCsv(allEmployeesReportRecords, fileName, fieldsToExport, [
    transformForCsvExport(fieldsToExport),
  ]);
};

type GetEmployeeDatatableLinkProps = {
  employerId: string;
  employeeId: string;
  isDon: boolean;
  impersonatedEmployeeId?: string;
  onboardingPeriodId?: string | null;
};

export const getEmployeeDatatableLink = ({
  onboardingPeriodId,
  employeeId,
  employerId,
  impersonatedEmployeeId,
  isDon,
}: GetEmployeeDatatableLinkProps) => {
  if (isDon) {
    return onboardingPeriodId
      ? APP_ROUTES.DON_EMPLOYEE_PLANS_TAB({
          employerId,
          employeeId,
          onboardingPeriodId,
          impersonatedEmployeeId,
        })
      : APP_ROUTES.DON_EMPLOYEE_PAGE({
          employerId,
          employeeId,
          impersonatedEmployeeId,
        });
  }

  return onboardingPeriodId
    ? APP_ROUTES.OPS_EMPLOYEE_PLANS_TAB({
        employerId,
        employeeId,
        onboardingPeriodId,
        impersonatedEmployeeId,
      })
    : APP_ROUTES.OPS_EMPLOYEE_PAGE({
        employerId,
        employeeId,
        impersonatedEmployeeId,
      });
};
